import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Tag,
  Typography,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { getTrackingDetails } from "../../api/dashboard.api";
import Card from "../../components/ui/Card";
import Spinner from "../../components/ui/Spinner";
import Table from "../../components/ui/Table";
import { formatDate, formatShortId } from "../../utils/formatters";

interface TrackingItem {
  session_id: string;
  tracking_number: string | null;
  date: string;
  ticket_created: boolean;
  messages: number;
}

// ── helpers ─────────────────────────────────────────────────────────────────
function parseRangeFromSearch(search: string): [Dayjs, Dayjs] {
  const p = new URLSearchParams(search);
  const period = p.get("period");
  if (period === "today") {
    return [dayjs().startOf("day"), dayjs().endOf("day")];
  }
  const start = p.get("start");
  const end = p.get("end");
  if (start && end) {
    return [dayjs(start), dayjs(end)];
  }
  return [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")];
}

function downloadCsv(items: TrackingItem[], start: Dayjs, end: Dayjs) {
  const headers = [
    "Tracking Number",
    "Session",
    "Date",
    "Messages",
    "Ticket Created",
  ];
  const rows = items.map(i => [
    i.tracking_number ?? "—",
    i.session_id,
    i.date,
    i.messages,
    i.ticket_created ? "Yes" : "No",
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tracking-${start.format("YYYYMMDD")}-${end.format("YYYYMMDD")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ────────────────────────────────────────────────────────────────
const TrackingDetailsPage = () => {
  const location = useLocation();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() =>
    parseRangeFromSearch(location.search)
  );
  const [items, setItems] = useState<TrackingItem[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getTrackingDetails({
        start_date: dateRange[0].toISOString(),
        end_date: dateRange[1].toISOString(),
      });
      setItems(
        (data.items ?? []).filter((i: TrackingItem) => i.tracking_number)
      );
    } catch {
      setError("Unable to load tracking details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [dateRange]);

  const filteredItems = search
    ? items.filter(
        i =>
          (i.tracking_number ?? "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          i.session_id.toLowerCase().includes(search.toLowerCase())
      )
    : items;

  const ticketCount = items.filter(i => i.ticket_created).length;
  const uniqueNums = new Set(items.map(i => i.tracking_number).filter(Boolean))
    .size;

  return (
    <div className="page-stack">
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Typography.Title level={2} style={{ margin: 0 }}>
          Tracking Inquiries
        </Typography.Title>
        <Button
          icon={<DownloadOutlined />}
          disabled={!items.length}
          onClick={() => downloadCsv(items, dateRange[0], dateRange[1])}
        >
          Export CSV
        </Button>
      </div>

      {/* Date range */}
      <Card>
        <Form layout="inline">
          <Form.Item label="From">
            <DatePicker
              value={dateRange[0]}
              onChange={v =>
                v && setDateRange([v.startOf("day"), dateRange[1]])
              }
            />
          </Form.Item>
          <Form.Item label="To">
            <DatePicker
              value={dateRange[1]}
              onChange={v => v && setDateRange([dateRange[0], v.endOf("day")])}
            />
          </Form.Item>
          <Form.Item>
            <Input.Search
              placeholder="Search tracking number or session..."
              allowClear
              style={{ width: 300 }}
              onChange={e => setSearch(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Card>

      {/* Summary cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Typography.Text type="secondary">Total Inquiries</Typography.Text>
            <Typography.Title level={3} style={{ margin: "4px 0 0" }}>
              {items.length}
            </Typography.Title>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Typography.Text type="secondary">
              Unique Tracking Numbers
            </Typography.Text>
            <Typography.Title level={3} style={{ margin: "4px 0 0" }}>
              {uniqueNums}
            </Typography.Title>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Typography.Text type="secondary">Tickets Created</Typography.Text>
            <Typography.Title level={3} style={{ margin: "4px 0 0" }}>
              {ticketCount}
            </Typography.Title>
          </Card>
        </Col>
      </Row>

      {error && <Alert type="error" showIcon message={error} />}

      {/* Grid */}
      {loading ? (
        <Spinner />
      ) : (
        <Card>
          <Table
            dataSource={filteredItems}
            rowKey="session_id"
            pagination={{ defaultPageSize: 50, showSizeChanger: true }}
            columns={[
              {
                title: "Tracking Number",
                dataIndex: "tracking_number",
                render: (v: string | null) =>
                  v ? (
                    <Typography.Text code copyable>
                      {v}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">—</Typography.Text>
                  ),
              },
              {
                title: "Session",
                dataIndex: "session_id",
                render: formatShortId,
              },
              {
                title: "Date",
                dataIndex: "date",
                render: (v: string) => formatDate(v),
              },
              {
                title: "Messages",
                dataIndex: "messages",
              },
              {
                title: "Ticket Created",
                dataIndex: "ticket_created",
                render: (v: boolean) =>
                  v ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                      Yes
                    </Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">
                      No
                    </Tag>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default TrackingDetailsPage;
